import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { System, SystemInsert, SystemUpdate, SYSTEM_FUNCTIONS } from '@/types/system';
import { useSystemStore } from '@/stores/systemStore';
import { useAuth } from '@/hooks/useAuth';
import { Info, X } from 'lucide-react';
import { toast } from 'sonner';

interface SystemFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  system?: System;
  zoneId: string;
}

const PROTOCOL_OPTIONS = ['TCP', 'UDP', 'HTTP', 'HTTPS', 'MQTT', 'OPC-UA', 'Modbus', 'EtherNet/IP', 'PROFINET', 'BACnet'];
const PII_TYPE_OPTIONS = ['Name', 'Email', 'Phone', 'Address', 'ID Number', 'Financial', 'Health', 'Biometric'];

export function SystemFormDialog({ open, onOpenChange, system, zoneId }: SystemFormDialogProps) {
  const { addSystem, updateSystem } = useSystemStore();
  const { profile } = useAuth();
  
  const [name, setName] = useState('');
  const [functionType, setFunctionType] = useState('');
  const [protocols, setProtocols] = useState<string[]>([]);
  const [owner, setOwner] = useState('');
  const [rto, setRto] = useState<number | undefined>();
  const [rpo, setRpo] = useState<number | undefined>();
  const [processesPii, setProcessesPii] = useState(false);
  const [piiTypes, setPiiTypes] = useState<string[]>([]);
  const [dpiaRequired, setDpiaRequired] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (system) {
      setName(system.name);
      setFunctionType(system.function || '');
      setProtocols(system.protocols || []);
      setOwner(system.owner || '');
      setRto(system.rto || undefined);
      setRpo(system.rpo || undefined);
      setProcessesPii(system.processes_pii || false);
      setPiiTypes(system.pii_types || []);
      setDpiaRequired(system.dpia_required || false);
    } else {
      resetForm();
    }
  }, [system, open]);

  const resetForm = () => {
    setName('');
    setFunctionType('');
    setProtocols([]);
    setOwner('');
    setRto(undefined);
    setRpo(undefined);
    setProcessesPii(false);
    setPiiTypes([]);
    setDpiaRequired(false);
  };

  const toggleProtocol = (protocol: string) => {
    setProtocols(prev =>
      prev.includes(protocol) ? prev.filter(p => p !== protocol) : [...prev, protocol]
    );
  };

  const togglePiiType = (type: string) => {
    setPiiTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error('System name is required');
      return;
    }
    
    if (!profile?.org_id) {
      toast.error('Organization not found');
      return;
    }

    setSubmitting(true);
    
    try {
      const systemData: SystemInsert | SystemUpdate = {
        name: name.trim(),
        function: functionType || undefined,
        protocols: protocols.length > 0 ? protocols : undefined,
        owner: owner || undefined,
        rto: rto || undefined,
        rpo: rpo || undefined,
        processes_pii: processesPii,
        pii_types: processesPii && piiTypes.length > 0 ? piiTypes : undefined,
        dpia_required: dpiaRequired,
      };

      if (system) {
        await updateSystem(system.id, systemData as SystemUpdate);
        toast.success('System updated successfully');
      } else {
        await addSystem({
          ...systemData,
          zone_id: zoneId,
          org_id: profile.org_id,
        } as SystemInsert);
        toast.success('System created successfully');
      }
      
      onOpenChange(false);
      resetForm();
    } catch (error) {
      console.error('Error saving system:', error);
      toast.error(system ? 'Failed to update system' : 'Failed to create system');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{system ? 'Edit System' : 'Create New System'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm">Basic Information</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="name">System Name *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Active Directory Server"
                  required
                />
              </div>

              <div className="col-span-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="function">Function</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>The primary function or purpose of this system</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Select value={functionType} onValueChange={setFunctionType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select function" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(SYSTEM_FUNCTIONS).map(([category, functions]) => (
                      <optgroup key={category} label={category}>
                        {functions.map((func) => (
                          <SelectItem key={func} value={func}>
                            {func}
                          </SelectItem>
                        ))}
                      </optgroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="owner">Owner</Label>
                <Input
                  id="owner"
                  value={owner}
                  onChange={(e) => setOwner(e.target.value)}
                  placeholder="System owner name"
                />
              </div>
            </div>
          </div>

          {/* Protocols */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm">Network Protocols</h3>
            <div className="flex flex-wrap gap-2">
              {PROTOCOL_OPTIONS.map((protocol) => (
                <Badge
                  key={protocol}
                  variant={protocols.includes(protocol) ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => toggleProtocol(protocol)}
                >
                  {protocol}
                  {protocols.includes(protocol) && <X className="ml-1 h-3 w-3" />}
                </Badge>
              ))}
            </div>
          </div>

          {/* Recovery Objectives */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm">Recovery Objectives</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="rto">RTO (hours)</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Recovery Time Objective: Maximum acceptable downtime</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Input
                  id="rto"
                  type="number"
                  min="0"
                  value={rto || ''}
                  onChange={(e) => setRto(e.target.value ? parseInt(e.target.value) : undefined)}
                  placeholder="e.g., 4"
                />
              </div>

              <div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="rpo">RPO (hours)</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Recovery Point Objective: Maximum acceptable data loss</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Input
                  id="rpo"
                  type="number"
                  min="0"
                  value={rpo || ''}
                  onChange={(e) => setRpo(e.target.value ? parseInt(e.target.value) : undefined)}
                  placeholder="e.g., 1"
                />
              </div>
            </div>
          </div>

          {/* Privacy & Data Protection */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm">Privacy & Data Protection</h3>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="processes_pii"
                checked={processesPii}
                onCheckedChange={(checked) => {
                  setProcessesPii(checked as boolean);
                  if (!checked) {
                    setPiiTypes([]);
                    setDpiaRequired(false);
                  }
                }}
              />
              <Label htmlFor="processes_pii" className="font-normal cursor-pointer">
                This system processes Personal Identifiable Information (PII)
              </Label>
            </div>

            {processesPii && (
              <>
                <div>
                  <Label>PII Types Processed</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {PII_TYPE_OPTIONS.map((type) => (
                      <Badge
                        key={type}
                        variant={piiTypes.includes(type) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => togglePiiType(type)}
                      >
                        {type}
                        {piiTypes.includes(type) && <X className="ml-1 h-3 w-3" />}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="dpia_required"
                    checked={dpiaRequired}
                    onCheckedChange={(checked) => setDpiaRequired(checked as boolean)}
                  />
                  <Label htmlFor="dpia_required" className="font-normal cursor-pointer">
                    Data Protection Impact Assessment (DPIA) required
                  </Label>
                </div>
              </>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
                resetForm();
              }}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving...' : system ? 'Update System' : 'Create System'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
